﻿import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import Loader from '@Elements/Loader';
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { I18nextProvider, useTranslation } from 'react-i18next';
import Context, { context_type } from '@Util/Context';
import i18n from '@Util/i18n';
import moment from 'moment';
import humanize_duration from 'humanize-duration';
import dayjs from 'dayjs';
import { registerLocale } from "react-datepicker";
import sv from 'date-fns/locale/sv';
//import * as Api from '@Util/Api';
import { api } from "@Util/Api";
import { apiBase, dashboard_pages } from '@Util/GlobalValues';
import Action_Prompt from '@Elements/Action_Prompt';
import Info_Prompt from '@Elements/Info_Prompt';
import Sidebar_Toggle from '@Elements/Sidebar_Toggle';
import Reminder_Notification from '@Elements/Reminder_Notification';

import {
    write_console,
    write_console_error,
    site_should_show_last_exception_warning,
    site_should_show_last_response_time_warning
} from '@Util/Utils';

import {
    Featured_Project,
    Current_Project,
    Info_Prompt_Information,
    Current_Project_Ellapsed_Time,
    Weeks_Timelog_Filter,
    Time_Log_Entry,
    InTime_User,
    Logged_In_User,
    Customer,
    Status_Update_Type,
    Prompt_Information_Type,
    Months_Timelog_Filter,
    Client_Website_Type,
    Client_Website_Warning_Type,
    Reminder
} from '@Util/Types';

registerLocale('sv', sv);

import Side_Navigation from '@Modules/Side_Navigation';
import Dynamic_Island from '@Modules/Dynamic_Island';
import Status_Update from '@Elements/Status_Update';
import Startpage from '@Pages/Startpage';

const Customerspage = lazy(() => import('@Pages/Customerspage'));
const Projectspage = lazy(() => import('@Pages/Projectspage'));
const Reportspage = lazy(() => import('@Pages/Reportspage'));
const Reminderspage = lazy(() => import('@Pages/Reminderspage'));
const Settingspage = lazy(() => import('@Pages/Settingspage'));
const Profilepage = lazy(() => import('@Pages/Profilepage'));
const Errorpage = lazy(() => import('@Pages/Errorpage'));
const ClientWebsitespage = lazy(() => import('@Pages/ClientWebsitespage'));
const SingleClientWebsitepage = lazy(() => import('@Pages/SingleClientWebsitespage'));

export default function InTime(): JSX.Element {
    const [t] = useTranslation();
    // INTIME DATA
    const [status_update, set_status_update] = useState<Status_Update_Type>({
        type: "Success",
        text: "",
        visible: false
    });
    const [featured_projects, set_featured_projects] = useState<Featured_Project[]>([]);
    const [current_project, set_current_project] = useState<Current_Project>(null);
    const [todays_time_log, set_todays_time_log] = useState<Time_Log_Entry[]>([]);
    const [weeks_time_log, set_weeks_time_log] = useState<Time_Log_Entry[]>([]);
    const [months_time_log, set_months_time_log] = useState<Time_Log_Entry[]>([]);
    const [logged_in_user, set_logged_in_user] = useState<Logged_In_User>({
        id: 0,
        firstName: "",
        lastName: "",
        isAdmin: false,
        email: "",
        photo: {
            fileName: "",
            fileUrl: ""
        },
        settings: {
            employeeRole: "",
            displayPomodoro: false,
            defaultShowTimelogTableOnStartpage: false,
            displayClientWebsiteErrors: false,
            displayNewInTimeVersion: false,
            displayModuleRecentProjects: false,
            displayModuleAllProjects: false
        }
    });
    const [intime_users, set_intime_users] = useState<InTime_User[]>([]);
    const [reminders, set_reminders] = useState<Reminder[]>([]);
    const [overdue_reminders, set_overdue_reminders] = useState<Reminder[]>([]);
    const [customers, set_customers] = useState<Customer[]>([]);
    const [client_websites, set_client_websites] = useState<Client_Website_Type[]>([]);
    const [client_websites_with_warnings, set_client_websites_with_warnings] = useState<Client_Website_Warning_Type[]>([]);
    const [bulletin_board, set_bulletin_board] = useState<string>("");
    
    // INTIME SETTINGS
    const [prompt_information, set_prompt_information] = useState<Prompt_Information_Type>({
        open: false,
        prompt_headline: "",
        prompt_text: "",
        on_cancel_action: null,
        on_accept_action: null
    });
    const [info_prompt_information, set_info_prompt_information] = useState<Info_Prompt_Information>({
        open: false,
        prompt_headline: "",
        prompt_text: "",
        on_accept_action: null
    });
    const [current_project_ellapsed_time, set_current_project_ellapsed_time] = useState<Current_Project_Ellapsed_Time>({
        humanized: "",
        simple: ""
    });
    const [weeks_timelog_filter, set_weeks_timelog_filter] = useState<Weeks_Timelog_Filter>({
        week_number: moment(),
        beginning_of_this_week: moment().startOf('week'),
        end_of_this_week: moment().endOf('week')
    });
    const [months_timelog_filter, set_months_timelog_filter] = useState<Months_Timelog_Filter>({
        beginning_of_this_month: moment().startOf('month'),
        end_of_this_month: moment().endOf('month')
    });
    const [sidebar_visible, set_sidebar_visible] = useState<boolean>(false);

    // CALCULATE TIME SPENT ON CURRENT PROJECT
    useEffect(() => {
        calculate_ellapsed_project_time();

        const interval = setInterval(() => {
            calculate_ellapsed_project_time();
        }, 60000);

        return () => clearInterval(interval);
    }, [current_project]);

    // FETCH INTIME DATA ON LOAD
    useEffect(() => {
        fetch_intime_data();
    }, []);

    // FETCH INTIME USERS AND THEIR ACTIVITY
    useEffect(() => {
        const interval = setInterval(() => {
            fetch_intime_users();
            fetch_client_websites();
            fetch_reminders();
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    // CHECK FOR OVERDUE REMINDERS
    useEffect(() => {
        const check_for_overdue_reminders = () => {
            const current_time = dayjs();  // Get the current time
            const overdue = reminders.filter(reminder => {
                // Parse the due date and compare with the current time
                return !reminder.isCompleted && dayjs(reminder.dueDate).isBefore(current_time);
            });

            // Update the overdue reminders state
            set_overdue_reminders(overdue);
        };

        if (reminders?.length > 0) {
            check_for_overdue_reminders();
        }
        
        // Run the overdue check every 2 minutes (120000 ms)
        const interval_id = setInterval(check_for_overdue_reminders, 120000);

        // Cleanup interval on component unmount
        return () => clearInterval(interval_id);
    }, [reminders]);

    // CALCULATE ARRAY OF CLIENT WEBSITE WARNINGS
    const calculate_client_website_warnings = useCallback(async (client_sites: Client_Website_Type[]) => {
        try {
            const website_array = client_sites.reduce<Client_Website_Warning_Type[]>((acc, website) => {
                if (!website?.id || !website?.lastUpdated) return acc;

                const show_last_exception_warning: boolean = site_should_show_last_exception_warning(website);
                const show_last_response_time_warning: boolean = site_should_show_last_response_time_warning(website);

                // IF LAST RESPONSE TIME IS MORE THAN THE SEVERE LIMIT
                if (show_last_response_time_warning) {
                    acc.push({
                        website_id: website.id,
                        warning_type: "response_time",
                        warning_value: website.lastResponseTime,
                        warning_time: website.lastUpdated
                    });
                }

                if (show_last_exception_warning) {
                    acc.push({
                        website_id: website.id,
                        warning_type: "exception_count",
                        warning_value: website.lastExceptionCount,
                        warning_time: website.lastUpdated
                    });
                }

                // IF THE CERTIFICATE EXPIRATION DATE IS LESS THAN 1 DAY AWAY
                if (website.certificateExpirationDate && moment(website.certificateExpirationDate).isBefore(moment().add(1, 'days'))) {
                    acc.push({
                        website_id: website.id,
                        warning_type: "ssl_expiration",
                        warning_value: website.certificateExpirationDate,
                        warning_time: website.lastUpdated
                    });
                }

                return acc;
            }, []);

            set_client_websites_with_warnings(website_array);
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [client_websites]);

    const calculate_ellapsed_project_time = useCallback(() => {
        try {
            let ellapsedTime = "0 h, 0 m";
            let ellapsedTimeSimple = "0";

            if (current_project?.customerName && current_project?.startDate) {
                const startTime = moment(current_project.startDate);
                const currentTime = moment();

                if (startTime.isSameOrBefore(currentTime)) {
                    //const diffMilliseconds = currentTime.diff(startTime);
                    //const diffHours = Math.floor(diffMilliseconds / (1000 * 60 * 60));
                    //const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60)) % 60;

                    const duration = moment.duration(currentTime.diff(startTime));
                    const diffHours = Math.floor(duration.asHours());
                    const diffMinutes = duration.minutes();
                    //const diffMilliseconds = duration.asMilliseconds();

                    ellapsedTime = `${diffHours} h, ${diffMinutes} m`;
                    ellapsedTimeSimple = Number(duration.asHours().toFixed(2)).toString();
                }
            }

            set_current_project_ellapsed_time({
                humanized: ellapsedTime,
                simple: ellapsedTimeSimple
            });
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [current_project, set_current_project_ellapsed_time, write_console_error]);

    const fetch_logged_in_user = useCallback(async () => {
        try {
            const current_user = await api.get(`${apiBase}/user`);
    
            set_logged_in_user(current_user?.data?.result);
    
            if (current_user?.status !== 200) {
                throw new Error("Fetching logged in user: Received an error from the API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_logged_in_user]);

    const fetch_intime_users = useCallback(async () => {
        try {
            //const users = await Api.apiFetch(`${apiBase}/user-all`);
            const users = await api.get(`${apiBase}/user-all`);
    
            write_console(users?.data?.result, 'Fetching all InTime users');
    
            set_intime_users(users?.data?.result);
    
            //if (users.status === "Error") {
            //    throw new Error("Fetching InTime users: Received an error from the API");
            //}
            if (users.status !== 200) {
                throw new Error("Fetching InTime users: Received an error from the API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_intime_users]);

    const fetch_reminders = useCallback(async () => {
        try {
            //const reminders = await Api.apiFetch(`${apiBase}/GetReminders`);
            const reminders = await api.get(`${apiBase}/GetReminders`);

            write_console(reminders, 'Fetching all InTime reminders');
            set_reminders(reminders?.data?.result);

            if (reminders.statusText !== "Ok") {
                throw new Error("Fetching InTime reminders: Received an error from the API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_intime_users]);

    const fetch_customers = useCallback(async () => {
        try {
           // const customers = await Api.apiFetch(`${apiBase}/ScheduleEntry/GetCustomer`);
            const customers = await api.get(`${apiBase}/ScheduleEntry/GetCustomer`);
    
            write_console(customers, 'Fetching all Initiva customers');
    
            set_customers(customers?.data?.result);
    
            //if (customers.status === "Error") {
            //    throw new Error("Fetching InTime customers: Received an error from the API");
            //}
            if (customers.status !== 200) {
                throw new Error("Fetching InTime customers: Received an error from the API");
            }
        } catch (err) {
            console.error(err.message);
        }
    }, [apiBase, set_customers]);

    const fetch_current_project = useCallback(async () => {
        try {
            //const current_project = await Api.apiFetch(`${apiBase}/ScheduleEntry/running`);
            const current_project = await api.get(`${apiBase}/ScheduleEntry/running`);
    
            set_current_project(current_project?.data?.result);
            write_console(current_project, 'Fetching current project');
    
            //if (current_project?.status === "Error") {
            //    throw new Error("Fetching current project: Error received from API");
            //}
            if (current_project?.status !== 200) {
                throw new Error("Fetching current project: Error received from API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_current_project]);

    const fetch_featured_projects = useCallback(async () => {
        try {
            //const featured_projects = await Api.callApi(`${apiBase}/ScheduleEntry/project/featured`, { quantity: 10 });
            const featured_projects = await api.post(`${apiBase}/ScheduleEntry/project/featured`, { quantity: 10 });
    
            write_console(featured_projects, 'Fetching featured projects');
            set_featured_projects(featured_projects?.data?.result);
    
            //if (featured_projects?.status === "Error") {
            //    throw new Error("Featured Projects: Error status received from API");
            //}
            if (featured_projects?.status !== 200) {
                throw new Error("Featured Projects: Error status received from API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_featured_projects]);

    const check_for_overlapping_times = useCallback(async (logArr: Time_Log_Entry[]): Promise<void> => {
        if (!logArr || logArr.length === 0) {
            return;
        }
    
        let prevStopTime = moment(logArr[0].endDate);
    
        for (let i = 1; i < logArr.length; i++) {
            const log: Time_Log_Entry = logArr[i];
            const currentStartTime: moment.Moment = moment(log.startDate);
    
            if (currentStartTime.isBefore(prevStopTime)) {
                set_info_prompt_information({
                    open: true,
                    prompt_headline: t('Text.OverlappingLogsHeadline'),
                    prompt_text: t('Text.OverlappingLogsDescription'),
                    on_accept_action: () => set_info_prompt_information({
                        open: false,
                        prompt_headline: "",
                        prompt_text: "",
                        on_accept_action: null
                    })
                });
                return;
            }
    
            prevStopTime = moment(log.endDate);
        }
    }, [set_info_prompt_information, t]);

    const fetch_todays_timelog = useCallback(async () => {
        try {
            const startDate: moment.Moment = moment().startOf('day');
            const endDate: moment.Moment = moment().endOf('day');
    
            //const logs = await Api.callApi(`${apiBase}/ScheduleEntry/Get`, {
            //    from: startDate.toJSON(),
            //    to: endDate.toJSON()
            //});

            const logs = await api.post(`${apiBase}/ScheduleEntry/Get`, {
                from: startDate.toJSON(),
                to: endDate.toJSON()
            });
    
            check_for_overlapping_times(logs?.data?.result);
    
            set_todays_time_log(logs?.data?.result);
    
            write_console(logs, 'Fetching todays timelog');
    
            //if (logs?.status === "Error") {
            //    throw new Error("Fetch todays logs: Received error from the API");
            //}
            if (logs?.status !== 200) {
                throw new Error("Fetch todays logs: Received error from the API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_todays_time_log, check_for_overlapping_times, write_console, write_console_error]);
    
    const fetch_this_weeks_timelog = useCallback(async () => {
        try {
            const startDate: moment.Moment = moment(weeks_timelog_filter.beginning_of_this_week);
            const endDate: moment.Moment = moment(weeks_timelog_filter?.end_of_this_week);
    
            //const logs = await Api.callApi(`${apiBase}/ScheduleEntry/Get`, {
            //    from: startDate.toJSON(),
            //    to: endDate.toJSON()
            //});

            const logs = await api.post(`${apiBase}/ScheduleEntry/Get`, {
                from: startDate.toJSON(),
                to: endDate.toJSON()
            });
    
            set_weeks_time_log(logs?.data?.result);
    
            write_console(logs, 'Fetching weeks timelog');
    
            //if (logs?.status === "Error") {
            //    throw new Error("Fetching timelog for this week: Received error from the API");
            //}
            if (logs?.status !== 200) {
                throw new Error("Fetching timelog for this week: Received error from the API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, weeks_timelog_filter, set_weeks_time_log, write_console, write_console_error]);
    
    const fetch_this_months_timelog = useCallback(async () => {
        try {
            const startDate: moment.Moment = moment(months_timelog_filter.beginning_of_this_month);
            const endDate: moment.Moment = moment(months_timelog_filter?.end_of_this_month);
    
            //const logs = await Api.callApi(`${apiBase}/ScheduleEntry/Get`, {
            //    from: startDate.toJSON(),
            //    to: endDate.toJSON()
            //});
            const logs = await api.post(`${apiBase}/ScheduleEntry/Get`, {
                from: startDate.toJSON(),
                to: endDate.toJSON()
            });
    
            set_months_time_log(logs?.data?.result);
    
            write_console(logs, 'Fetching months timelog');
    
            //if (logs?.status === "Error") {
            //    throw new Error("Fetching timelog for this month: Received error from the API");
            //}
            if (logs?.status !== 200) {
                throw new Error("Fetching timelog for this month: Received error from the API");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, months_timelog_filter, set_months_time_log, write_console, write_console_error]);

    async function create_default_entry(id: number): Promise<void> {
        try {
            //const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/default-create`, { projectId: id });
            const api_call = await api.post(`${apiBase}/ScheduleEntry/default-create`, { projectId: id });

            write_console(api_call, 'Running: Create default log object');

            //if (api_call?.status === "Error") {
            //    throw new Error("Create default time log: Error happened");
            //}
            if (api_call?.status !== 200) {
                throw new Error("Create default time log: Error happened");
            }

            if (api_call?.data?.result?.id > 0) {
                fetch_todays_timelog();
            }

            fetch_current_project();
            fetch_featured_projects();
            fetch_this_weeks_timelog();
            fetch_intime_users();
        } catch (err) {
            write_console_error(err, err.message);
        }
    }

    const start_time_function = useCallback(async (id: number): Promise<void> => {
        try {
            set_current_project_ellapsed_time({ humanized: "", simple: "" });
    
            //const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/Create`, { projectId: id });
            const api_call = await api.post(`${apiBase}/ScheduleEntry/Create`, { projectId: id });
    
            write_console(api_call, 'Running: Start Time');
    
            //if (api_call?.status === "Error") {
            //    throw new Error("Start time: Received error from the API");
            //}
            if (api_call?.status !== 200) {
                throw new Error("Start time: Received error from the API");
            }
    
            if (api_call?.data?.result?.id > 0) {
                fetch_todays_timelog();
            }
                
            fetch_current_project();
            fetch_featured_projects();
            fetch_this_weeks_timelog();
            fetch_intime_users();
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_current_project_ellapsed_time, fetch_todays_timelog, fetch_current_project, fetch_featured_projects, fetch_this_weeks_timelog, fetch_intime_users, write_console, write_console_error]);
    
    const start_time = useCallback(async (id: number): Promise<void> => {
        try {
            if (current_project?.customerName && current_project?.startDate && moment.utc(current_project.startDate).isAfter(moment.utc('0001-01-01T00:00:00'))) {
                const previous_project_start_time: moment.Moment = moment(current_project.startDate);
                const time_duration: moment.Duration = moment.duration(previous_project_start_time.diff(moment()));
    
                if (time_duration.asHours() < -10) {
                    set_info_prompt_information({
                        open: true,
                        prompt_headline: t('Text.WorkedLongTime'),
                        prompt_text: t('Text.WorkedLongTimeDescription', {
                            humanized_duration: humanize_duration(time_duration, { language: "sv", units: ["h"], maxDecimalPoints: 0 }),
                            customer_name: current_project.customerName,
                            project_name: current_project?.projectName
                        }),
                        on_accept_action: () => start_time_function(id)
                    });
                }
            }
    
            return start_time_function(id);
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [current_project, set_info_prompt_information, t, start_time_function, write_console_error]);

    const stop_time_function = useCallback(async (id: string): Promise<void> => {
        try {
            set_current_project_ellapsed_time({ humanized: "", simple: "" });
    
            //const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/stop`, { scheduleEntryId: id });
            const api_call = await api.post(`${apiBase}/ScheduleEntry/stop`, { scheduleEntryId: id });
    
            write_console(api_call, 'Running: Stop Time');
    
            //if (api_call?.status === "Error") {
            //    throw new Error("Stop time: Received error from the API");
            //}
            if (api_call?.status !== 200) {
                throw new Error("Stop time: Received error from the API");
            }
    
            fetch_todays_timelog();
            fetch_this_weeks_timelog();
            fetch_featured_projects();
            fetch_current_project();
            fetch_intime_users();
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_current_project_ellapsed_time, fetch_todays_timelog, fetch_this_weeks_timelog, fetch_featured_projects, fetch_current_project, fetch_intime_users, write_console, write_console_error]);

    const stop_time = useCallback(async (id: string): Promise<void> => {
        try {
            if (current_project?.startDate && moment.utc(current_project?.startDate).isAfter(moment.utc('0001-01-01T00:00:00'))) {
                let previous_project_start_time: moment.Moment = await moment(current_project?.startDate);
                let time_duration: moment.Duration = await moment.duration(previous_project_start_time.diff(moment()));
    
                if (time_duration.asHours() < -10) {
                    return set_info_prompt_information({
                        open: true,
                        prompt_headline: t('Text.WorkedLongTime'),
                        prompt_text: t('Text.WorkedLongTimeDescription', {
                            humanized_duration: humanize_duration(time_duration, { language: "sv", units: ["h"], maxDecimalPoints: 0 }),
                            customer_name: current_project.customerName,
                            project_name: current_project?.projectName
                        }),
                        on_accept_action: () => {
                            stop_time_function(id);
                            set_info_prompt_information({
                                open: false,
                                prompt_headline: "",
                                prompt_text: "",
                                on_accept_action: null
                            })
                        }
                    });
                } else {
                    return stop_time_function(id);
                }
            } else {
                return stop_time_function(id);
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [current_project, set_info_prompt_information, t, stop_time_function, write_console_error, info_prompt_information]);

    const delete_time_object = useCallback(async (id: number): Promise<void> => {
        try {
            //const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/Delete`, {
            //    scheduleEntryId: id
            //});
            const api_call = await api.post(`${apiBase}/ScheduleEntry/Delete`, {
                scheduleEntryId: id
            });

            if (api_call?.status !== 200) {
                set_status_update({
                    type: "Error",
                    text: t('Text.GeneralError'),
                    visible: true
                });
            } else {
                set_status_update({
                    type: "Success",
                    text: t('Text.ObjectDeleted'),
                    visible: true
                });
            }

            write_console(api_call, 'Running: Delete Time Object');
    
            fetch_todays_timelog();
            fetch_this_weeks_timelog();
    
            if (id === current_project.id) {
                window.location.reload();
            } 
        } catch (err) {
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
            write_console_error(err, err.message);
        }
    }, [apiBase, set_status_update, t, write_console, fetch_todays_timelog, fetch_this_weeks_timelog, current_project, write_console_error]);
    
    const update_time_object = useCallback(async (schedule_entry_id: number, start_time: string, end_time: string, project_id: number): Promise<void> => {
        try {
            //const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/Update`, {
            //    scheduleEntryId: schedule_entry_id,
            //    StartTime: start_time,
            //    EndTime: end_time,
            //    projectId: project_id
            //});
            const api_call = await api.post(`${apiBase}/ScheduleEntry/Update`, {
                scheduleEntryId: schedule_entry_id,
                StartTime: start_time,
                EndTime: end_time,
                projectId: project_id
            });

            if (api_call?.status !== 200) {
                set_status_update({
                    type: "Error",
                    text: t('Text.GeneralError'),
                    visible: true
                });
            } else {
                set_status_update({
                    type: "Success",
                    text: t('Text.ObjectUpdated'),
                    visible: true
                });
            }
    
            write_console(api_call, 'Running: Update Time Object');
    
            fetch_todays_timelog();
            fetch_this_weeks_timelog();
            calculate_ellapsed_project_time();
        } catch (err) {
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
            write_console_error(err, err.message);
        }
    }, [apiBase, set_status_update, t, write_console, fetch_todays_timelog, fetch_this_weeks_timelog, write_console_error]);

    const split_time_object = useCallback(async (id: string): Promise<void> => {
        try {
            //const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/Split`, {
            //    scheduleEntryId: id
            //});
            const api_call = await api.post(`${apiBase}/ScheduleEntry/Split`, {
                scheduleEntryId: id
            });

            if (api_call?.status !== 200) {
                set_status_update({
                    type: "Error",
                    text: t('Text.GeneralError'),
                    visible: true
                });
            } else {
                set_status_update({
                    type: "Success",
                    text: t('Text.ObjectUpdated'),
                    visible: true
                });
            }
    
            write_console(api_call, 'Running: Split Time Object');
    
            fetch_todays_timelog();
            fetch_this_weeks_timelog();
            calculate_ellapsed_project_time();
        } catch (err) {
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
            write_console_error(err, err.message);
        }
    }, [apiBase, set_status_update, t, write_console, fetch_todays_timelog, fetch_this_weeks_timelog, write_console_error]);
    
    const align_time_object = useCallback(async (id: string, mode: string): Promise<void> => {
        try {
            //const api_call = await Api.callApi(`${apiBase}/ScheduleEntry/Align`, {
            //    scheduleEntryId: id,
            //    mode
            //});
            const api_call = await api.post(`${apiBase}/ScheduleEntry/Align`, {
                scheduleEntryId: id,
                mode
            });

            if (api_call?.status !== 200) {
                set_status_update({
                    type: "Error",
                    text: t('Text.GeneralError'),
                    visible: true
                });
            } else {
                set_status_update({
                    type: "Success",
                    text: t('Text.ObjectUpdated'),
                    visible: true
                });
            }

            write_console(api_call, 'Running: Align Time Object');
    
            fetch_todays_timelog();
            fetch_this_weeks_timelog();
            calculate_ellapsed_project_time();
        } catch (err) {
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
            write_console_error(err, err.message);
        }
    }, [apiBase, set_status_update, t, write_console, fetch_todays_timelog, fetch_this_weeks_timelog, write_console_error]);

    const fetch_client_websites = useCallback(async () => {
        try {
            //const client_sites = await Api.apiFetch(`${apiBase}/ClientSites`);
            const client_sites = await api.get(`${apiBase}/ClientSites`);

            write_console(client_sites, 'Fetching Initiva Client Sites');

            set_client_websites(client_sites?.data?.result);

            calculate_client_website_warnings(client_sites?.data?.result);

            //if (client_sites.status === "Error") {
            //    throw new Error("Fetching client websites: Error happened");
            //}
            if (client_sites.status !== 200) {
                throw new Error("Fetching client websites: Error happened");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [client_websites, set_client_websites]);

    const fetch_bulletin_board = useCallback(async () => {
        try {
            //const bulletin_board = await Api.apiFetch(`${apiBase}/GlobalReminder`);
            const bulletin_board = await api.get(`${apiBase}/GlobalReminder`);

            write_console(bulletin_board, 'Fetching bulletin board');

            set_bulletin_board(bulletin_board?.data?.result);

            //if (bulletin_board.status === "Error") {
            //    throw new Error("Fetching bulletin board: Error happened");
            //}
            if (bulletin_board.status !== 200) {
                throw new Error("Fetching bulletin board: Error happened");
            }
        } catch (err) {
            write_console_error(err, err.message);
        }
    }, [apiBase, set_bulletin_board]);

    const fetch_intime_data = useCallback(async () => {
        await Promise.all([
            fetch_logged_in_user(),
            fetch_current_project(),
            fetch_featured_projects(),
            fetch_intime_users(),
            fetch_customers(),
            fetch_todays_timelog(),
            fetch_client_websites(),
            fetch_bulletin_board(),
            fetch_reminders()
        ]);
    }, [
        fetch_logged_in_user,
        fetch_current_project,
        fetch_featured_projects,
        fetch_intime_users,
        fetch_customers,
        fetch_todays_timelog
    ]);

    const context_value: context_type = {
        // DATA
        status_update,
        set_status_update,
        weeks_timelog_filter,
        set_weeks_timelog_filter,
        featured_projects,
        current_project,
        current_project_ellapsed_time,
        logged_in_user,
        intime_users,
        customers,
        todays_time_log,
        weeks_time_log,
        months_time_log,
        prompt_information,
        set_prompt_information,
        client_websites,
        client_websites_with_warnings,
        sidebar_visible,
        set_sidebar_visible,
        bulletin_board,
        reminders,
        overdue_reminders,

        // API CALLS
        create_default_entry,
        start_time,
        stop_time,
        fetch_customers,
        fetch_this_weeks_timelog,
        fetch_this_months_timelog,
        fetch_bulletin_board,
        fetch_reminders,
        delete_time_object,
        update_time_object,
        split_time_object,
        align_time_object
    };

    return (
        <Router>
            <Context.Provider value={context_value}>
                <I18nextProvider i18n={i18n}>
                    <div className={`InTime ${sidebar_visible ? "InTime--Navigation_Visible" : ""}`}>
                        <Side_Navigation />
                        <main className="Page_Content">
                            <Suspense fallback={<Loader />}>
                                <div className="Page_Content_Inner">
                                    <Dynamic_Island />
                                    <Routes>
                                        { /* STARTPAGE */}
                                        <Route
                                            path={dashboard_pages.home}
                                            element={<Startpage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* CUSTOMERS */}
                                        <Route
                                            path={dashboard_pages.customers}
                                            element={<Customerspage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* PROJETS */}
                                        <Route
                                            path={dashboard_pages.projects}
                                            element={<Projectspage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* REPORTS */}
                                        <Route
                                            path={dashboard_pages.report}
                                            element={<Reportspage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* REMINDERS */}
                                        <Route
                                            path={dashboard_pages.reminders}
                                            element={<Reminderspage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* SETTINGS PAGE */}
                                        <Route
                                            path={dashboard_pages.settings}
                                            element={<Settingspage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* PROFILE PAGE */}
                                        <Route
                                            path={dashboard_pages.profile}
                                            element={<Profilepage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* CLIENT WEBSITES PAGE */}
                                        <Route
                                            path={dashboard_pages.customer_websites}
                                            element={<ClientWebsitespage />}
                                            errorElement={<Errorpage />}
                                        />
                                        { /* SINGLE CLIENT WEBSITE PAGE */}
                                        <Route
                                            path={dashboard_pages.customer_websites + "/:id"}
                                            element={<SingleClientWebsitepage />}
                                            errorElement={<Errorpage />}
                                        />
                                        <Route path="*" element={<Navigate to="/d" />} />
                                    </Routes>
                                </div>
                            </Suspense>
                        </main>
                        <Action_Prompt
                            open={prompt_information.open}
                            on_accept_action={prompt_information.on_accept_action}
                            on_cancel_action={prompt_information.on_cancel_action}
                            prompt_headline={prompt_information.prompt_headline}
                            prompt_text={prompt_information.prompt_text}
                        />
                        <Info_Prompt 
                            open={info_prompt_information.open}
                            prompt_headline={info_prompt_information.prompt_headline}
                            prompt_text={info_prompt_information.prompt_text}
                            on_accept_action={info_prompt_information.on_accept_action}
                            on_cancel_action={() => console.log("Cancelled")}
                        />
                        <Status_Update />
                        <Sidebar_Toggle />
                        <Reminder_Notification />
                    </div>
                </I18nextProvider>
            </Context.Provider>
        </Router>
    );
}